import { useEffect, useState, useMemo } from "react";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import API from "../api/axios";
import { useToast } from "../components/Toast";
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  InputAdornment,
  Stack,
  Tooltip,
  LinearProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import PeopleIcon from "@mui/icons-material/People";
import TuneIcon from "@mui/icons-material/Tune";
import { NAVY, ACCENT } from "../theme";
import DateFilter from "../components/ui/DateFilter";
import RefreshButton from "../components/ui/RefreshButton";

const STATUS_COLOR = {
  PENDING: "default",
  SHORTLISTED: "success",
  SELECTED: "info",
  L1_ROUND: "info",
  L2_ROUND: "primary",
  ON_HOLD: "warning",
  REJECTED: "error",
};
const statusLabel = (k) =>
  ({
    PENDING: "Pending",
    SHORTLISTED: "Shortlisted",
    SELECTED: "Selected",
    L1_ROUND: "L1 Round",
    L2_ROUND: "L2 Round",
    ON_HOLD: "On Hold",
    REJECTED: "Rejected",
  })[k] ||
  k ||
  "Pending";

const filterBtns = [
  { key: "ALL", label: "All" },
  { key: "SHORTLISTED", label: "Shortlisted" },
  { key: "SELECTED", label: "Selected" },
  { key: "L1_ROUND", label: "L1 Round" },
  { key: "L2_ROUND", label: "L2 Round" },
  { key: "ON_HOLD", label: "On Hold" },
  { key: "REJECTED", label: "Rejected" },
];

const fmtDate = (value) => {
  if (!value) return "—";
  const s = String(value);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  return s;
};

// Timezone-safe YYYY-MM-DD, matching the staff pages' date-filter behavior
const dateOnly = (value) => {
  if (!value) return null;
  const s = String(value);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[1]}-${m[2]}-${m[3]}` : null;
};

const todayStr = (() => {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
})();

function fullName(c) {
  return (
    `${c.firstName || ""} ${c.lastName || ""}`.trim() ||
    c.candidateName ||
    "Unnamed"
  );
}

// Same columns as the admin Candidates table, minus the Actions column
const TABLE_HEADERS = [
  "Job Code",
  "Date",
  "Recruiter",
  "First Name",
  "Last Name",
  "Phone",
  "LinkedIn",
  "Email",
  "PAN",
  "Role",
  "Current Company",
  "Domain",
  "Skills",
  "Relevant Skills",
  "Exp",
  "Curr Sal",
  "Exp Sal",
  "Location",
  "Pref Loc",
  "Resume",
  "Comments",
  "Status",
];

export default function ClientCandidates() {
  const toast = useToast();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("ALL");
  const [jobCodeFilter, setJobCodeFilter] = useState("");
  const [recruiterFilter, setRecruiterFilter] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const fetchAll = (spin = false) => {
    if (spin) setLoading(true);
    API.get("/client/candidates")
      .then((r) => setList(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const jobCodes = useMemo(
    () => [...new Set(list.map((c) => c.jobCode).filter(Boolean))].sort(),
    [list],
  );
  const recruiterNames = useMemo(
    () => [...new Set(list.map((c) => c.recruiterName).filter(Boolean))].sort(),
    [list],
  );

  const visible = useMemo(
    () =>
      list.filter((c) => {
        const sq = search.toLowerCase();
        const matchesSearch =
          !sq ||
          fullName(c).toLowerCase().includes(sq) ||
          c.recruiterName?.toLowerCase().includes(sq) ||
          c.companyName?.toLowerCase().includes(sq) ||
          c.jobCode?.toLowerCase().includes(sq);
        const matchesStatus = filter === "ALL" || c.feedback === filter;
        const matchesJobCode = !jobCodeFilter || c.jobCode === jobCodeFilter;
        const matchesRecruiter =
          !recruiterFilter || c.recruiterName === recruiterFilter;
        const ds = dateOnly(c.candidateDate || c.createdAt);
        const matchesDate =
          (!fromDate && !toDate) ||
          (ds && (!fromDate || ds >= fromDate) && (!toDate || ds <= toDate));
        return (
          matchesSearch &&
          matchesStatus &&
          matchesJobCode &&
          matchesRecruiter &&
          matchesDate
        );
      }),
    [list, search, filter, jobCodeFilter, recruiterFilter, fromDate, toDate],
  );

  const clearFilters = () => {
    setJobCodeFilter("");
    setRecruiterFilter("");
    setFromDate("");
    setToDate("");
  };

  // Export the currently VISIBLE (filtered) rows to an Excel file — same
  // shape as the admin Candidates export, minus internal-only fields.
  const exportToExcel = () => {
    const rows = visible.map((c) => ({
      "Job Code": c.jobCode,
      Date: fmtDate(c.candidateDate),
      "Recruiter Name": c.recruiterName,
      "First Name": c.firstName,
      "Last Name": c.lastName,
      Phone: c.phone,
      Email: c.email,
      "LinkedIn URL": c.linkedinUrl,
      "Current Role": c.currentRole,
      "Current Company": c.companyName,
      Domain: c.domain,
      Skills: c.skills,
      "Relevant Skills": c.relevantSkills,
      Experience: c.totalExperience || c.experienceYears || "",
      "Current Salary": c.currentSalary,
      "Expected Salary": c.expectedSalary,
      "Current Location": c.currentLocation,
      "Expected Location": c.expectedLocation,
      Status: statusLabel(c.feedback),
      Comments: c.comments,
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Candidates");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([buf], { type: "application/octet-stream" }),
      `Candidates_${todayStr}.xlsx`,
    );
    toast.success(
      `Exported ${rows.length} candidate${rows.length !== 1 ? "s" : ""}`,
    );
  };

  const downloadResume = async (c) => {
    try {
      const res = await API.get(`/client/candidates/${c.id}/resume`, {
        responseType: "blob",
      });
      saveAs(
        new Blob([res.data]),
        c.resumeFilename || `resume_${c.firstName || c.id}`,
      );
    } catch {
      toast.error("Could not download resume");
    }
  };

  const LinkedInText = ({ url }) =>
    url ? (
      <Tooltip title={url}>
        <span
          style={{
            display: "block",
            maxWidth: 180,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontSize: 12,
            color: "#64748b",
          }}
        >
          {url}
        </span>
      </Tooltip>
    ) : (
      <Typography sx={{ fontSize: 13, color: "text.disabled" }}>—</Typography>
    );

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, md: 4 } }}>
        <LinearProgress />
        <Typography sx={{ mt: 2, color: "text.secondary" }}>
          Loading candidates…
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: "100%", overflowX: "hidden" }}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 3,
          pl: { xs: 5, md: 0 },
        }}
      >
        <Box>
          <Typography variant="h4">Candidates</Typography>
          <Typography sx={{ color: "text.secondary", mt: 0.5, fontSize: 14 }}>
            {visible.length} of {list.length} candidates for your positions
          </Typography>
        </Box>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
        >
          <DateFilter
            from={fromDate}
            to={toDate}
            onFromChange={setFromDate}
            onToChange={setToDate}
            isToday={fromDate === todayStr && toDate === todayStr}
            isAllTime={!fromDate && !toDate}
            onToday={() => {
              setFromDate(todayStr);
              setToDate(todayStr);
            }}
            onAllTime={() => {
              setFromDate("");
              setToDate("");
            }}
          />
          <RefreshButton onClick={() => fetchAll(true)} />
          {visible.length > 0 && (
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={exportToExcel}
            >
              Export Excel
            </Button>
          )}
        </Stack>
      </Box>

      <TextField
        placeholder="Search candidate, recruiter, company or job code…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack
          direction="row"
          spacing={1}
          flexWrap="wrap"
          useFlexGap
          sx={{ mb: 2 }}
        >
          {filterBtns.map((f) => {
            const active = filter === f.key;
            return (
              <Chip
                key={f.key}
                label={f.label}
                onClick={() => setFilter(f.key)}
                variant={active ? "filled" : "outlined"}
                sx={{
                  fontWeight: 600,
                  bgcolor: active ? NAVY : "transparent",
                  color: active ? "#fff" : "text.primary",
                  "&:hover": { bgcolor: active ? NAVY : "action.hover" },
                }}
              />
            );
          })}
        </Stack>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          alignItems={{ sm: "center" }}
          flexWrap="wrap"
          useFlexGap
        >
          <TuneIcon
            sx={{
              color: "text.secondary",
              display: { xs: "none", sm: "block" },
            }}
          />
          <TextField
            select
            size="small"
            label="Job Code"
            value={jobCodeFilter}
            onChange={(e) => setJobCodeFilter(e.target.value)}
            sx={{ minWidth: 150, flex: 1 }}
          >
            <MenuItem value="">All Job Codes</MenuItem>
            {jobCodes.map((jc) => (
              <MenuItem key={jc} value={jc}>
                {jc}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Recruiter"
            value={recruiterFilter}
            onChange={(e) => setRecruiterFilter(e.target.value)}
            sx={{ minWidth: 150, flex: 1 }}
          >
            <MenuItem value="">All Recruiters</MenuItem>
            {recruiterNames.map((n) => (
              <MenuItem key={n} value={n}>
                {n}
              </MenuItem>
            ))}
          </TextField>
          {(jobCodeFilter || recruiterFilter) && (
            <Button
              color="error"
              variant="outlined"
              size="small"
              startIcon={<CloseIcon sx={{ fontSize: 15 }} />}
              onClick={clearFilters}
            >
              Clear
            </Button>
          )}
        </Stack>
      </Paper>

      {visible.length === 0 ? (
        <Paper sx={{ textAlign: "center", py: 7, px: 2 }}>
          <PeopleIcon sx={{ fontSize: 44, color: ACCENT, mb: 1 }} />
          <Typography sx={{ fontWeight: 700 }}>No candidates found</Typography>
          <Typography sx={{ color: "text.secondary", fontSize: 13, mt: 0.5 }}>
            {search ||
            filter !== "ALL" ||
            jobCodeFilter ||
            recruiterFilter ||
            fromDate ||
            toDate
              ? "Try clearing your filters or search."
              : "Candidates for your positions will appear here."}
          </Typography>
        </Paper>
      ) : (
        <Paper sx={{ overflow: "hidden" }}>
          <TableContainer>
            <Table stickyHeader sx={{ minWidth: 1900 }}>
              <TableHead>
                <TableRow>
                  {TABLE_HEADERS.map((h) => (
                    <TableCell
                      key={h}
                      sx={{
                        whiteSpace: "nowrap",
                        fontSize: 11,
                        fontWeight: 700,
                      }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {visible.map((c) => (
                  <TableRow key={c.id} hover>
                    <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                      {c.jobCode || "—"}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {fmtDate(c.candidateDate)}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {c.recruiterName}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {c.firstName || "—"}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {c.lastName || "—"}
                    </TableCell>
                    <TableCell>{c.phone || "—"}</TableCell>
                    <TableCell sx={{ maxWidth: 200 }}>
                      <LinkedInText url={c.linkedinUrl} />
                    </TableCell>
                    <TableCell>
                      {c.email ? (
                        <Tooltip title={c.email}>
                          <span
                            style={{
                              display: "block",
                              maxWidth: 180,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {c.email}
                          </span>
                        </Tooltip>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                      {c.panNumber || "—"}
                    </TableCell>
                    <TableCell>{c.currentRole || "—"}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {c.companyName || "—"}
                    </TableCell>
                    <TableCell>{c.domain || "—"}</TableCell>
                    <TableCell>{c.skills || "—"}</TableCell>
                    <TableCell>{c.relevantSkills || "—"}</TableCell>
                    <TableCell align="center">
                      {c.totalExperience ||
                        (c.experienceYears ? `${c.experienceYears}yr` : "—")}
                    </TableCell>
                    <TableCell>{c.currentSalary || "—"}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>
                      {c.expectedSalary || "—"}
                    </TableCell>
                    <TableCell>{c.currentLocation || "—"}</TableCell>
                    <TableCell>{c.expectedLocation || "—"}</TableCell>
                    <TableCell>
                      {c.hasResume ? (
                        <Button
                          size="small"
                          startIcon={<DownloadIcon sx={{ fontSize: 15 }} />}
                          onClick={() => downloadResume(c)}
                          sx={{ textTransform: "none" }}
                        >
                          Download
                        </Button>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell
                      sx={{ width: 240, minWidth: 240, maxWidth: 240 }}
                    >
                      {c.comments ? (
                        <Tooltip
                          title={
                            <span style={{ whiteSpace: "pre-wrap" }}>
                              {c.comments}
                            </span>
                          }
                        >
                          <Typography
                            sx={{
                              fontSize: 13,
                              lineHeight: 1.5,
                              display: "-webkit-box",
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "normal",
                              wordBreak: "break-word",
                            }}
                          >
                            {c.comments}
                          </Typography>
                        </Tooltip>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={statusLabel(c.feedback)}
                        color={STATUS_COLOR[c.feedback] || "default"}
                        variant="outlined"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
}
